import React, { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { markOnboardingTask } from "../lib/onboarding";
import { X, Sparkles, Send, Terminal } from "lucide-react";
import { useI18n } from "../i18n/I18nContext";

const VERDICT_RE = /\nVERDICT:(INTERESTING|WATCH|PASS)\s*$/;

function extractVerdict(text) {
  const m = text.match(VERDICT_RE);
  if (!m) return { text, verdict: null };
  return { text: text.slice(0, m.index).trimEnd(), verdict: m[1] };
}

export default function AIChatPanel({ open, listing, onClose, savedSessionId, onSessionSaved, onVerdictUpdate }) {
  const { t, lang } = useI18n();

  const SUGGESTIONS = [
    t("aiChat.suggestion1"),
    t("aiChat.suggestion2"),
    t("aiChat.suggestion3"),
    t("aiChat.suggestion4"),
    t("aiChat.suggestion5"),
  ];

  const listingGreeting = (l) => {
    if (!l) return t("aiChat.greetingDefault");
    return t("aiChat.greetingListing", {
      title: l.title,
      market: l.market_name,
      faktor: l.faktor,
      zone: l.zone,
      lage: l.lage_grade,
      price: (l.price / 1000).toFixed(0),
      size: l.size_m2,
      cf: l.monthly_cashflow ?? 0,
    });
  };

  const initMessages = (l) => [{ role: "ai", text: listingGreeting(l) }];

  const [messages, setMessages] = useState(() => initMessages(listing));
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [closeWarning, setCloseWarning] = useState(false);
  const [pendingAutoAssess, setPendingAutoAssess] = useState(!!listing);
  const [viewportHeight, setViewportHeight] = useState(null);
  const scrollRef = useRef(null);
  const textareaRef = useRef(null);
  const prevListingId = useRef(listing?.id);

  // Mobile virtual keyboards resize window.visualViewport without resizing the
  // layout viewport on several browser/OS combinations — the CSS 100dvh unit on
  // the panel (below) handles most of this, but isn't reliably keyboard-aware
  // everywhere, so it's the classic "chat input hides behind the keyboard" bug.
  // Track the real visual viewport height directly as the more robust fallback.
  useEffect(() => {
    if (!open || typeof window === "undefined" || !window.visualViewport) return;
    const vv = window.visualViewport;
    const update = () => setViewportHeight(vv.height);
    update();
    vv.addEventListener("resize", update);
    vv.addEventListener("scroll", update);
    return () => {
      vv.removeEventListener("resize", update);
      vv.removeEventListener("scroll", update);
    };
  }, [open]);

  const autoResize = (el) => {
    el.style.height = "auto";
    const h = Math.min(el.scrollHeight, 200);
    el.style.height = `${h}px`;
    el.style.overflowY = el.scrollHeight > 200 ? "auto" : "hidden";
  };

  // Keep a ref so the listing-change effect can read savedSessionId without depending on it
  const savedSessionIdRef = useRef(savedSessionId);
  savedSessionIdRef.current = savedSessionId;

  useEffect(() => {
    if (listing?.id === prevListingId.current) return;
    prevListingId.current = listing?.id;
    setMessages(initMessages(listing));
    setCloseWarning(false);
    const sid = savedSessionIdRef.current;
    if (sid) {
      // Resume saved session — skip auto-assess, load history from backend
      setPendingAutoAssess(false);
      api.get(`/ai/history/${sid}`).then(({ data }) => {
        if (data.messages?.length > 0) {
          setMessages(data.messages);
          setSessionId(sid);
        } else {
          setSessionId(null);
          setPendingAutoAssess(!!listing);
        }
      }).catch(() => {
        setSessionId(null);
        setPendingAutoAssess(!!listing);
      });
    } else {
      setSessionId(null);
      setPendingAutoAssess(!!listing);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listing?.id]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, busy]);

  const buildContext = () => {
    if (!listing) return undefined;
    return {
      listing_focus: {
        id: listing.id,
        title: listing.title,
        market: listing.market_name,
        country: listing.country,
        price: listing.price,
        faktor: listing.faktor,
        zone: listing.zone,
        lage_grade: listing.lage_grade,
        size_m2: listing.size_m2,
        rooms: listing.rooms,
        postcode: listing.postcode,
        url: listing.url,
        annual_net_rent: listing.annual_net_rent,
        monthly_cashflow: listing.monthly_cashflow,
        mortgage_monthly: listing.mortgage_monthly,
        vs_markt_pct: listing.vs_markt_pct,
        price_drop_pct: listing.price_drop_pct,
        days_on_market: listing.days_on_market,
        days_in_system: listing.days_in_system,
        condition_score: listing.condition_score,
        flags: (listing.condition_tags || listing.flags || []).slice(0, 20),
      },
    };
  };

  // Persist session ID whenever it's first assigned — lets AppShell link listing → session
  useEffect(() => {
    if (!sessionId || !listing?.id || !onSessionSaved) return;
    onSessionSaved(listing.id, sessionId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const sendHidden = async (msg) => {
    setBusy(true);
    try {
      const { data } = await api.post("/ai/chat", {
        message: msg,
        session_id: sessionId,
        context: buildContext(),
        lang,
      });
      setSessionId((prev) => prev ?? data.session_id);
      const { text, verdict } = extractVerdict(data.reply);
      setMessages((m) => [...m, { role: "ai", text }]);
      if (verdict && listing?.id && onVerdictUpdate) onVerdictUpdate(listing.id, verdict);
    } catch (e) {
      setMessages((m) => [...m, { role: "error", text: formatApiError(e) }]);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (!open || !pendingAutoAssess) return;
    setPendingAutoAssess(false);
    sendHidden(
      "Give me a concise investment assessment of this listing. Include: " +
      "(1) overall verdict — is this deal interesting and why, in 2–3 sentences; " +
      "(2) risk profile — key risks specific to this deal (not generic); " +
      "(3) what to watch for on a site visit or in DD; " +
      "(4) profitable entry price — at what Kaufpreis does this deal hit GREEN " +
      "(use the correct Faktor threshold for this market's Lage grade: A-Lage ≤20, " +
      "B-Lage ≤18, weaker ≤16.7)? State the target price in €, the required discount " +
      "from ask in %, and one sentence on whether that discount is realistic for this listing. " +
      "Highlight anything extraordinary — positive or negative. " +
      "Call get_listing_details first to get the full record including portal description."
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, pendingAutoAssess]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    if (textareaRef.current) { textareaRef.current.style.height = "auto"; textareaRef.current.style.overflowY = "hidden"; }
    setMessages((m) => [...m, { role: "user", text: msg }]);
    setBusy(true);
    try {
      const { data } = await api.post("/ai/chat", { message: msg, session_id: sessionId, context: buildContext(), lang });
      markOnboardingTask("ai");
      if (!sessionId) setSessionId(data.session_id);
      const { text: cleanText, verdict } = extractVerdict(data.reply);
      setMessages((m) => [...m, { role: "ai", text: cleanText }]);
      if (verdict && listing?.id && onVerdictUpdate) onVerdictUpdate(listing.id, verdict);
    } catch (e) {
      setMessages((m) => [...m, { role: "error", text: formatApiError(e) }]);
    } finally {
      setBusy(false);
    }
  };

  const handleClose = () => {
    if (messages.length > 1 && !closeWarning) {
      setCloseWarning(true);
      setTimeout(() => setCloseWarning(false), 4000);
      return;
    }
    setCloseWarning(false);
    onClose();
  };

  if (!open) return null;

  return (
    <>
      <div
        onClick={handleClose}
        className="fixed inset-0 z-40"
        data-testid="ai-chat-backdrop"
      />
      <aside
        data-testid="ai-chat-panel"
        className="fixed top-0 right-0 z-50 h-dvh w-full sm:w-[480px] lg:w-[560px] bg-bg border-l border-border ai-glow flex flex-col"
        style={viewportHeight ? { height: `${viewportHeight}px` } : undefined}
      >
        {/* HEADER */}
        <header className="border-b border-border px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-signal-green grid place-items-center">
              <Terminal size={16} className="text-black" />
            </div>
            <div>
              <div className="font-heading font-black tracking-tight text-lg leading-none">IMMO-AI</div>
              {listing ? (
                <div className="overline mt-1 text-signal-green truncate max-w-[280px]" title={listing.title}>
                  {listing.market_name} · F{listing.faktor} · {listing.zone}
                </div>
              ) : (
                <div className="overline mt-1">{t("aiChat.modelDesk")}</div>
              )}
            </div>
          </div>
          <button
            onClick={handleClose}
            data-testid="ai-chat-close"
            className="text-text-muted hover:text-white p-2"
          >
            <X size={18} />
          </button>
        </header>

        {closeWarning && (
          <div className="border-b border-signal-yellow/40 bg-signal-yellow/10 px-5 py-2.5 flex items-center justify-between gap-3">
            <span className="text-[11px] font-mono text-signal-yellow">{t("aiChat.clearWarning")}</span>
            <button
              onClick={() => { setCloseWarning(false); onClose(); }}
              className="text-[11px] font-mono text-white hover:text-signal-yellow whitespace-nowrap"
            >
              {t("aiChat.closeAnyway")}
            </button>
          </div>
        )}

        {/* MESSAGES */}
        <div className="flex-1 min-h-0 overflow-hidden relative scanline">
          <div ref={scrollRef} className="h-full overflow-y-auto px-5 py-5 space-y-4">
            {messages.map((m, i) => (
              <Message key={i} m={m} />
            ))}
            {busy && (
              <div className="text-[11px] font-mono text-signal-green flex items-center gap-2">
                <span className="block w-1.5 h-1.5 bg-signal-green dot-pulse" />
                {t("aiChat.computing")}
              </div>
            )}
          </div>
        </div>

        {/* SUGGESTIONS */}
        {messages.length <= 1 && (
          <div className="px-5 py-3 border-t border-border space-y-2">
            <div className="overline">{t("aiChat.try")}</div>
            <div className="flex flex-wrap gap-2">
              {SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  onClick={() => send(s)}
                  data-testid="ai-suggestion"
                  className="text-left text-[11px] font-mono text-text-secondary hover:text-signal-green border border-border hover:border-signal-green px-2.5 py-1.5 transition-colors"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* INPUT */}
        <form
          onSubmit={(e) => { e.preventDefault(); send(); }}
          className="border-t border-border p-3 flex items-end gap-2"
          style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))" }}
        >
          <div className="flex-1 flex items-end gap-2 px-3 py-2 bg-surface border border-border focus-within:border-signal-green">
            <span className="text-signal-green font-mono text-sm leading-6">▌</span>
            <textarea
              ref={textareaRef}
              data-testid="ai-chat-input"
              value={input}
              rows={1}
              onChange={(e) => { setInput(e.target.value); autoResize(e.target); }}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder={t("aiChat.inputPlaceholder")}
              className="flex-1 bg-transparent outline-none text-sm font-mono text-white placeholder:text-text-muted resize-none overflow-y-hidden leading-6"
              disabled={busy}
            />
          </div>
          <button
            type="submit"
            disabled={busy || !input.trim()}
            data-testid="ai-chat-send"
            className="px-4 py-3 bg-signal-green text-black hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <Send size={14} />
          </button>
        </form>
      </aside>
    </>
  );
}

function Message({ m }) {
  if (m.role === "user") {
    return (
      <div className="flex justify-end">
        <div className="max-w-[85%] bg-white text-black px-3 py-2 text-sm font-mono">
          {m.text}
        </div>
      </div>
    );
  }
  if (m.role === "error") {
    return (
      <div className="border border-signal-red/40 bg-signal-red/10 px-3 py-2 text-xs font-mono text-signal-red">
        ⚠ {m.text}
      </div>
    );
  }
  return (
    <div className="reveal">
      <div className="overline mb-1 text-signal-green inline-flex items-center gap-2">
        <Sparkles size={11} /> IMMO-AI
      </div>
      <div className="text-sm leading-relaxed text-text-primary font-mono">
        <FormattedText text={m.text} />
      </div>
    </div>
  );
}

function inlineFormat(text, keyPrefix = "") {
  // Handle **bold**, *italic*, `code` inline
  const segments = text.split(/(\*\*[^*\n]+\*\*|\*[^*\n]+\*|`[^`\n]+`)/g);
  return segments.map((seg, i) => {
    if (seg.startsWith("**") && seg.endsWith("**"))
      return <strong key={`${keyPrefix}${i}`} className="text-white font-bold">{seg.slice(2, -2)}</strong>;
    if (seg.startsWith("*") && seg.endsWith("*"))
      return <em key={`${keyPrefix}${i}`} className="text-text-secondary not-italic opacity-80">{seg.slice(1, -1)}</em>;
    if (seg.startsWith("`") && seg.endsWith("`"))
      return <code key={`${keyPrefix}${i}`} className="text-signal-green bg-zinc-900/80 px-1 py-0.5 text-[11px]">{seg.slice(1, -1)}</code>;
    return seg || null;
  });
}

function FormattedText({ text }) {
  const lines = text.split("\n");
  const elements = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Fenced code block
    if (line.startsWith("```")) {
      const codeLines = [];
      i++;
      while (i < lines.length && !lines[i].startsWith("```")) {
        codeLines.push(lines[i]);
        i++;
      }
      elements.push(
        <pre key={i} className="bg-zinc-900 border border-border px-3 py-2 text-[11px] overflow-x-auto my-2 text-signal-green">
          {codeLines.join("\n")}
        </pre>
      );
      i++;
      continue;
    }

    // Horizontal rule
    if (/^---+$/.test(line.trim())) {
      elements.push(<hr key={i} className="border-border my-3" />);
      i++; continue;
    }

    // Headings
    if (line.startsWith("### ")) {
      elements.push(<div key={i} className="text-[10px] uppercase tracking-widest text-text-muted font-bold mt-3 mb-1">{inlineFormat(line.slice(4), `h${i}`)}</div>);
      i++; continue;
    }
    if (line.startsWith("## ")) {
      elements.push(<div key={i} className="font-bold text-white mt-3 mb-1">{inlineFormat(line.slice(3), `h${i}`)}</div>);
      i++; continue;
    }
    if (line.startsWith("# ")) {
      elements.push(<div key={i} className="font-black text-white mt-3 mb-1 text-base">{inlineFormat(line.slice(2), `h${i}`)}</div>);
      i++; continue;
    }

    // Unordered list
    if (/^[-*] /.test(line)) {
      elements.push(
        <div key={i} className="flex gap-2 my-0.5 leading-relaxed">
          <span className="text-signal-green shrink-0 select-none">·</span>
          <span>{inlineFormat(line.slice(2), `li${i}`)}</span>
        </div>
      );
      i++; continue;
    }

    // Numbered list
    const numMatch = line.match(/^(\d+)\. (.*)/);
    if (numMatch) {
      elements.push(
        <div key={i} className="flex gap-2 my-0.5 leading-relaxed">
          <span className="text-signal-green shrink-0 font-bold select-none">{numMatch[1]}.</span>
          <span>{inlineFormat(numMatch[2], `nl${i}`)}</span>
        </div>
      );
      i++; continue;
    }

    // Empty line
    if (line.trim() === "") {
      elements.push(<div key={i} className="h-2" />);
      i++; continue;
    }

    // Default paragraph
    elements.push(<div key={i} className="my-0.5 leading-relaxed">{inlineFormat(line, `p${i}`)}</div>);
    i++;
  }

  return <>{elements}</>;
}
